/**
 * Logging Service
 *
 * A centralized logging service that can manage multiple logger implementations.
 * Allows adding/removing loggers and broadcasts log messages to all registered loggers.
 *
 * @module services/shared
 */

import { LogLevel } from './ILogger';
import type { ILogger } from './ILogger';

/**
 * Configuration for the LoggingService
 */
export interface LoggingServiceConfig {
  /**
   * Default log level for the service
   */
  defaultLevel?: LogLevel;

  /**
   * Whether the service is enabled globally
   */
  enabled?: boolean;

  /**
   * Maximum number of loggers that can be registered
   */
  maxLoggers?: number;
}

/**
 * Default configuration for LoggingService
 */
const DEFAULT_SERVICE_CONFIG: Required<LoggingServiceConfig> = {
  defaultLevel: LogLevel.LOG,
  enabled: true,
  maxLoggers: 10
};

/**
 * Centralized logging service that manages multiple logger implementations
 */
export class LoggingService {
  private loggers: Map<string, ILogger> = new Map();
  private config: Required<LoggingServiceConfig>;

  constructor(config: LoggingServiceConfig = {}) {
    this.config = { ...DEFAULT_SERVICE_CONFIG, ...config };
  }

  /**
   * Add a logger to the service
   * @param name - Unique name for the logger
   * @param logger - Logger implementation
   * @returns true if logger was added successfully, false if name already exists or max loggers reached
   */
  addLogger(name: string, logger: ILogger): boolean {
    if (this.loggers.has(name)) {
      console.warn(`Logger with name '${name}' already exists. Use updateLogger() to replace it.`);
      return false;
    }

    if (this.loggers.size >= this.config.maxLoggers) {
      console.warn(`Maximum number of loggers (${this.config.maxLoggers}) reached. Cannot add logger '${name}'.`);
      return false;
    }

    this.loggers.set(name, logger);
    return true;
  }

  /**
   * Remove a logger from the service
   * @param name - Name of the logger to remove
   * @returns true if logger was removed, false if logger doesn't exist
   */
  removeLogger(name: string): boolean {
    return this.loggers.delete(name);
  }

  /**
   * Update an existing logger or add a new one
   * @param name - Name of the logger
   * @param logger - Logger implementation
   */
  updateLogger(name: string, logger: ILogger): void {
    this.loggers.set(name, logger);
  }

  /**
   * Get a logger by name
   * @param name - Name of the logger
   * @returns Logger instance or undefined if not found
   */
  getLogger(name: string): ILogger | undefined {
    return this.loggers.get(name);
  }

  /**
   * Get all registered logger names
   * @returns Array of logger names
   */
  getLoggerNames(): string[] {
    return Array.from(this.loggers.keys());
  }

  /**
   * Get the number of registered loggers
   * @returns Number of loggers
   */
  getLoggerCount(): number {
    return this.loggers.size;
  }

  /**
   * Clear all loggers
   */
  clearLoggers(): void {
    this.loggers.clear();
  }

  /**
   * Check if the service has any loggers
   * @returns true if at least one logger is registered
   */
  hasLoggers(): boolean {
    return this.loggers.size > 0;
  }

  /**
   * Log a debug message to all registered loggers
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  debug(message: string, ...args: unknown[]): void {
    if (!this.config.enabled) {
      return;
    }

    this.loggers.forEach((logger) => {
      try {
        logger.debug(message, ...args);
      } catch (error) {
        console.error('Error in logger during debug:', error);
      }
    });
  }

  /**
   * Log an informational message to all registered loggers
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  log(message: string, ...args: unknown[]): void {
    if (!this.config.enabled) {
      return;
    }

    this.loggers.forEach((logger) => {
      try {
        logger.log(message, ...args);
      } catch (error) {
        console.error('Error in logger during log:', error);
      }
    });
  }

  /**
   * Log a warning message to all registered loggers
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  warn(message: string, ...args: unknown[]): void {
    if (!this.config.enabled) {
      return;
    }

    this.loggers.forEach((logger) => {
      try {
        logger.warn(message, ...args);
      } catch (error) {
        console.error('Error in logger during warn:', error);
      }
    });
  }

  /**
   * Log an error message to all registered loggers
   * @param message - The message to log
   * @param args - Additional arguments to log
   */
  error(message: string, ...args: unknown[]): void {
    if (!this.config.enabled) {
      return;
    }

    this.loggers.forEach((logger) => {
      try {
        logger.error(message, ...args);
      } catch (error) {
        console.error('Error in logger during error:', error);
      }
    });
  }

  /**
   * Update service configuration
   * @param config - Partial configuration to update
   */
  updateConfig(config: Partial<LoggingServiceConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current service configuration
   * @returns Current configuration
   */
  getConfig(): Required<LoggingServiceConfig> {
    return { ...this.config };
  }

  /**
   * Enable or disable the logging service
   * @param enabled - Whether to enable the service
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Check if the logging service is enabled
   * @returns true if enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }
}